import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUrlString, useUrlBool, useUrlRangeOrNull } from '../../hooks/useUrlState';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation, confirmAction } from '../../lib/confirm';
import { Card, Typography, Tag, Select, Space, Button, Modal, Input, InputNumber, Popconfirm, Checkbox, DatePicker, Descriptions, Tooltip, message, StatCard } from '../../ui';
import { Plus, Check, X, Banknote, Stamp, Wallet, Hourglass, CircleCheckBig, Ban } from 'lucide-react';
import { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { useFocusRow } from '../../hooks/useFocusRow';
import { FileUploadField } from '../../components/FileUploadField';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { UserLink } from '../../components/EntityLinks';
import { usePermission, useHasRole } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useServerSort } from '../../hooks/useServerSort';
import { formatIst, formatIstDate } from '../../lib/time';
import { inr2 } from '../../lib/format';
// The app's shared bank-account option (name + last4 + a green Primary chip), so an account
// looks the same in this picker as on the Bank Details page and every payment form.
import { bankAcctOption } from '../billing/PaymentFormModal';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const STATUS_COLOR: Record<string, string> = {
  REQUESTED: 'gold', APPROVED: 'blue', DISBURSED: 'green', FUNDED_FROM_CASH: 'gold', REJECTED: 'red',
};
/**
 * What each state MEANS to the person reading the queue, rather than what the database calls it.
 *
 * "APPROVED" in a list headed "pending" reads as a contradiction — approved and pending at once —
 * when in fact the two open states are pending DIFFERENT things: one needs a decision, the other
 * needs someone to hand the money over. Naming the next action removes the question.
 */
const STATUS_LABEL: Record<string, string> = {
  REQUESTED: 'Awaiting approval',
  APPROVED: 'Awaiting payment',
  DISBURSED: 'Paid',
  /*
   * APPROVED BUT NEVER PAID OUT, and the label has to say so in one line.
   *
   * The rep already held enough of the company's collected cash, so that cash was moved from their
   * collection money into their wallet and nothing left a company account. Calling it "Approved" would
   * leave them waiting for a payment that is never coming; "Paid" would describe a payout that never happened.
   */
  FUNDED_FROM_CASH: 'From collection cash — nothing paid out',
  REJECTED: 'Rejected',
};
const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'];
// Plain language for the picker. The stored values are SCREAMING_SNAKE constants, and "BANK_TRANSFER"
// is both a database artefact and the string that was being truncated to "BANK_TRAN…".
/**
 * ONE SENTENCE FOR ONE FACT, wherever the request's supporting document is shown.
 *
 * The record view said "not attached · sent by the field staff" and the payout form, two clicks away,
 * said "none — this request predates the attachment rule". Same document, same absence, two different
 * explanations, and only one of them told the payer whether anybody had done anything wrong.
 *
 * (Proof is mandatory on the request form, so an empty one genuinely is older than that rule.)
 */
const PROOF_NOTE = (url?: string | null) =>
  url ? 'attached by the field staff with this request' : 'none — this request predates the attachment rule';

const MODE_LABEL: Record<string, string> = {
  CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank transfer', CHEQUE: 'Cheque', CARD: 'Card', OTHER: 'Other',
};
// Sentinel for "this advance is for me" in the Sales Head beneficiary picker (a real user id
// is never sent for self — the backend defaults an omitted `forUserId` to the requester).
const SELF = '__SELF__';

// Short role labels for the "by-whom" capsules.
const ROLE_LABEL: Record<string, string> = {
  SALES_PERSON: 'SP', SALES_HEAD: 'SH', ACCOUNTS: 'Accounts', ADMIN: 'Admin', SUPER_ADMIN: 'SA',
};
const roleTag = (role?: string | null) =>
  role ? <Tag tone="blue" style={{ marginInlineEnd: 0 }}>{ROLE_LABEL[role] ?? role.replace(/_/g, ' ')}</Tag> : null;

/**
 * A person in a cell: their NAME as readable, clickable text, with the role as a quiet suffix and
 * the date under it.
 *
 * Names used to sit inside a blue capsule. A chip is for a short controlled value — a status, a
 * role, a count — and a human name is neither: capsuled, it reads as a label rather than a person,
 * and a column of them turns a quiet table into a wall of blue pills. The role, which IS a
 * controlled value, keeps its capsule where it earns one.
 */
const personCell = (id?: string | number | null, name?: string | null, role?: string | null, at?: string | null) =>
  name
    ? (
      <div className="min-w-0">
        {/* Name first as clickable TEXT, role beside it as a badge — the role is a controlled value
            and reads as one; the name is a person and reads as a link. The date, when there is one,
            sits quietly underneath. */}
        <div className="flex min-w-0 flex-nowrap items-center gap-1.5">
          <span className="truncate type-body-medium"><UserLink id={id ?? undefined} role={role}>{name}</UserLink></span>
          <span className="shrink-0">{roleTag(role)}</span>
        </div>
        {at && <div className="type-caption text-subtle">{formatIstDate(at)}</div>}
      </div>
    )
    : <span className="text-subtle">—</span>;
const byCell = (name?: string | null, role?: string | null, at?: string | null, id?: string | number | null) =>
  personCell(id, name, role, at);

// Advance approval queue: rep requests → manager/admin approves → admin disburses (posts the
// ADVANCE_IN ledger row that moves money into the rep's wallet).
export function AdvanceRequestsPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  // Requesting needs BOTH the permission and a field-staff role. The role half can't be folded
  // into the permission check: SUPER_ADMIN holds '*', so the permission alone is true for an
  // admin who has no wallet to top up. `useHasRole` covers additional roles too (dual-hat users).
  const isHead = useHasRole('SALES_HEAD');
  const isFieldStaff = useHasRole('SALES_PERSON', 'SALES_HEAD');
  const canRequest = usePermission('ADVANCE.REQUEST') && isFieldStaff;
  const canApprove = usePermission('ADVANCE.APPROVE');
  const canDisburse = usePermission('EXPENSE.APPROVE.ACCT');

  const canSeeAll = canApprove || canDisburse || isHead;
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  const [showHistory, setShowHistory] = useUrlBool('history');
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  /* Seeded from `?rep=` so the register can be opened ON one person: the wallets list says
     "3 to pay · ₹605" and that has to be openable, or it is a claim the reader must take on trust.
     Read once, on mount - after that the picker owns it, so clearing the filter is not fought by
     the URL. */
  const [searchParams, setSearchParams] = useSearchParams();
  const [repId, setRepId] = useUrlString<string | undefined>('rep', undefined);
  const [range, setRange] = useUrlRangeOrNull('range');
  const [requestOpen, setRequestOpen] = useState(false);
  const [payNow, setPayNow] = useState(false);

  // Sales-person options for the filter (managers/admins only).
  const { data: salesPersons } = useQuery({
    queryKey: ['advance-salespersons'],
    queryFn: () => request<any[]>({ url: '/wallets' }),
    enabled: canSeeAll,
  });

  // Server-side column sort (backend ORDER BY over the full result set, not just this page).
  const { sortState, onSortChange, sort } = useServerSort();

  // Default to active (still-actionable) requests; the history toggle / a status / search /
  // date filter widens it to the full history.
  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status) p.status = status;
    else if (!showHistory && !search.trim() && !range) p.active = 'true';
    if (repId) p.repId = repId;
    if (search.trim()) p.search = search.trim();
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    if (sort) p.sort = sort;
    return p;
  }, [status, showHistory, repId, search, range, sort]);
  const { data, isLoading } = useQuery({
    queryKey: ['advance-requests', params],
    queryFn: () => request<any[]>({ url: '/advance-requests', params }),
    placeholderData: keepPreviousData,
  });

  /* The cards are counted by the server across the whole scoped set, and they drop `status` — a
     card that obeyed its own filter would read zero the instant you clicked it. */
  const statParams = useMemo(() => {
    const { status: _s, active: _a, sort: _so, ...rest } = params as any;
    return rest;
  }, [params]);
  const { data: stats } = useQuery({
    queryKey: ['advance-request-stats', statParams],
    queryFn: () => request<any>({ url: '/advance-requests/stats', params: statParams }),
    placeholderData: keepPreviousData,
  });

  // An approvals-hub / notification link names the request it meant (?focus=…) — open that row
  // rather than dropping the reader at the top of the list to hunt for it.
  const [detailFor, setDetailFor] = useState<any>(null);
  /* A `?focus=` link from the approvals hub opens the request's DIALOG — the hook's own escape
     hatch for a list whose detail is not an expanded row. */
  const focus = useFocusRow(data, { match: ['id', 'requestNo'], onFocus: (r: any) => setDetailFor(r) });

  const act = useMutation({
    mutationFn: ({ id, action, data }: { id: string; action: string; data?: any }) =>
      request({ url: `/advance-requests/${id}/${action}`, method: 'PATCH', data }),
    onSuccess: () => { message.success('Done'); qc.invalidateQueries({ queryKey: ['advance-requests'] }); qc.invalidateQueries({ queryKey: ['wallets'] }); qc.invalidateQueries({ queryKey: ['wallet-stats'] }); qc.invalidateQueries({ queryKey: ['badges'] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  /** A card is a filter you can click off again. DISBURSED/REJECTED live outside the default
      "still open" window, so choosing one also opens the history. */
  const pickStatus = (next: string, history = false) => {
    const on = status === next;
    setStatus(on ? undefined : next);
    setShowHistory(on ? false : history);
  };
  const oldestFirst = sortState?.key === 'createdAt' && sortState?.dir === 'asc';

  const columns: DataTableColumn<any>[] = [
    /* Widths are declared per column so the space is SHARED OUT rather than handed to whoever
       happens to render first. Purpose — the free text a reader actually scans — had no width and
       fell back to the same 150px slot as a date, so it truncated to "APPROVALS-…" while a column
       holding "03 Jul 2026" kept room to spare. It now gets the widest slot, and the ones with a
       known shape (dates, a status chip, an amount) are held to what they need. */
    {
      key: 'requestNo', title: 'Request No', dataIndex: 'requestNo', sortable: true, alwaysVisible: true, width: 170,
      render: (v: any) => <span className="whitespace-nowrap">{v}</span>,
    },
    {
      key: 'repName', title: 'Field staff', dataIndex: 'repName', width: 180,
      render: (v: any, r: any) => (
        <span onClick={(e) => e.stopPropagation()}>{personCell(r.repId, v ?? `#${r.repId}`, r.repRole)}</span>
      ),
    },
    { key: 'amount', title: 'Amount', dataIndex: 'amount', sortable: true, align: 'right', width: 104, render: (v: any) => <b>{inr2(v)}</b> },
    { key: 'purpose', title: 'Purpose', dataIndex: 'purpose', ellipsis: true, width: 260, render: (v: any) => v || '—' },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 150, render: (s: string) => <Tag tone={STATUS_COLOR[s] as any} className="whitespace-nowrap">{STATUS_LABEL[s] ?? s}</Tag> },
    { key: 'createdAt', title: 'Requested', dataIndex: 'createdAt', sortable: true, width: 116, render: (v: string) => formatIstDate(v) },
    { key: 'approvedByName', title: 'Approved by', dataIndex: 'approvedByName', width: 196, render: (_: any, r: any) => <span onClick={(e) => e.stopPropagation()}>{byCell(r.approvedByName, r.approvedByRole, r.approvedAt, r.approvedBy)}</span> },
    {
      key: 'disbursed', title: 'Disbursed', dataIndex: 'disbursedAt', sortable: true, width: 172,
      /* Date, then how it was paid, as plain text. The mode used to sit in a Tag that stretched to
         the full cell width — a grey bar per row saying "CASH" in a database voice, which shouted
         louder than the date it belonged to. */
      render: (_: any, r: any) => r.status === 'DISBURSED'
        ? (
          <div className="min-w-0">
            <div className="type-body-medium text-brand-700 dark:text-brand-400">{formatIstDate(r.disbursedAt)}</div>
            {r.paymentMode && (
              <div className="truncate type-caption text-subtle" title={[MODE_LABEL[r.paymentMode] ?? r.paymentMode, r.paymentReference].filter(Boolean).join(' · ')}>
                {[MODE_LABEL[r.paymentMode] ?? r.paymentMode, r.paymentReference].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        )
        /* A dash here means "not paid yet". For a request funded from collected cash it means "never
           going to be paid", which is a completely different thing to be told at a glance. */
        : r.status === 'FUNDED_FROM_CASH'
          ? <span className="type-caption text-warning-600 dark:text-harvest-300">Nothing paid — moved from collection cash</span>
          : <span className="text-subtle">—</span>,
    },
    { key: 'disbursedByName', title: 'Disbursed by', dataIndex: 'disbursedByName', defaultVisible: false, render: (_: any, r: any) => <span onClick={(e) => e.stopPropagation()}>{byCell(r.disbursedByName, r.disbursedByRole, null, r.disbursedBy)}</span> },
    {
      /* What the field staff attached when they asked. It is mandatory on every new request, so a
         blank cell here now means "raised before the rule existed" and not "nobody bothered" — the
         column is the fastest way to see that at a glance across the queue. */
      key: 'requestProofUrl', title: 'Document', dataIndex: 'requestProofUrl', align: 'center', width: 92,
      render: (v: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <AttachmentCell files={v ? [v] : []} label="Supporting document" size={34} />
        </span>
      ),
    },
    {
      // A receipt is a PICTURE — a "View" link made every reader open a tab to find out whether the
      // proof was the right document at all. The shared attachment cell shows the thumbnail and
      // opens the full-size viewer in place, same as orders and farmer KYC.
      key: 'paymentProofUrl', title: 'Receipt', dataIndex: 'paymentProofUrl', align: 'center', width: 92,
      render: (v: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <AttachmentCell files={v ? [v] : []} label="Payment proof" size={34} />
        </span>
      ),
    },
    { key: 'rejectReason', title: 'Note', dataIndex: 'rejectReason', ellipsis: true, width: 152, render: (v: any, r: any) => r.status === 'REJECTED' ? <span style={{ color: 'var(--color-danger-500)' }}>{v || 'Rejected'}</span> : '—' },
    {
      // Icon-only actions (tooltip + aria-label carry the wording), in the request's own order:
      // disburse · approve · reject. A row shows only the ones its status allows, never in a
      // different place.
      key: 'actions', title: 'Action', alwaysVisible: true, width: 110,
      render: (_: any, r: any) => (
        <Space wrap size={4} onClick={(e) => e.stopPropagation()}>
          {r.status === 'APPROVED' && canDisburse && (
            <Tooltip label="Disburse the advance">
              <Button size="sm" variant="primary" aria-label="Disburse the advance" icon={<Banknote size={16} />} onClick={(e: any) => { e.stopPropagation(); setPayNow(true); setDetailFor(r); }} />
            </Tooltip>
          )}
          {r.status === 'REQUESTED' && canApprove && (
            <>
              <Tooltip label="Approve request">
                <Popconfirm title="Approve this request?" okText="Approve" onConfirm={() => act.mutate({ id: r.id, action: 'approve' })}>
                  <Button size="sm" variant="primary" aria-label="Approve request" icon={<Check size={16} />} />
                </Popconfirm>
              </Tooltip>
              <RejectButton onReject={(reason) => confirmAction({ title: 'Reject this advance request?', content: 'No money moves, and your reason goes back to whoever asked.', okText: 'Yes, reject', danger: true }, () => act.mutate({ id: r.id, action: 'reject', data: { reason } }))} />
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Advance Requests</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canRequest && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setRequestOpen(true)}>Request advance</Button>
          )}
        </div>
      </div>
      {/* The queue holds two different waits, and money sitting in each. A card names the wait, the
          rupees behind it and how many requests that is — then filters the register down to exactly
          those rows, and back again when clicked a second time. The figures come from the server
          over the whole scoped set and ignore the status filter, so no card can zero itself out. */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label="Awaiting approval"
          tone={stats?.awaitingApproval?.count ? 'gold' : 'neutral'}
          icon={<Stamp />}
          value={<span className={`tabular-nums ${stats?.awaitingApproval?.count ? '' : 'text-subtle'}`}>{inr(stats?.awaitingApproval?.amount)}</span>}
          hint={stats?.awaitingApproval?.count ? `${stats.awaitingApproval.count} need${stats.awaitingApproval.count === 1 ? 's' : ''} a decision` : 'nothing to decide'}
          active={status === 'REQUESTED'}
          onClick={() => pickStatus('REQUESTED')}
        />
        <StatCard
          label="Awaiting payment"
          tone={stats?.awaitingPayment?.count ? 'sky' : 'neutral'}
          icon={<Wallet />}
          value={<span className={`tabular-nums ${stats?.awaitingPayment?.count ? '' : 'text-subtle'}`}>{inr(stats?.awaitingPayment?.amount)}</span>}
          hint={stats?.awaitingPayment?.count ? `${stats.awaitingPayment.count} approved, not handed over` : 'nothing owed out'}
          active={status === 'APPROVED'}
          onClick={() => pickStatus('APPROVED')}
        />
        {/* Not money — time. Whether the queue is being worked or merely exists. */}
        <StatCard
          label="Oldest still open"
          tone={(stats?.oldestOpenDays ?? 0) >= 7 ? 'warning' : 'neutral'}
          icon={<Hourglass />}
          value={
            stats?.oldestOpenDays == null
              ? <span className="text-subtle">—</span>
              : <span className={`tabular-nums ${stats.oldestOpenDays >= 7 ? 'text-warning-600 dark:text-harvest-300' : ''}`}>{stats.oldestOpenDays}d</span>
          }
          hint={stats?.oldestOpenNo ?? 'no open requests'}
          active={oldestFirst}
          onClick={() => {
            // Show the open queue with the longest wait at the top — the card's own number first.
            if (oldestFirst) { onSortChange({ key: 'createdAt', dir: 'desc' }); return; }
            setStatus(undefined); setShowHistory(false); onSortChange({ key: 'createdAt', dir: 'asc' });
          }}
        />
        <StatCard
          label="Paid this month"
          tone="brand"
          icon={<CircleCheckBig />}
          value={<span className={`tabular-nums ${stats?.paidThisMonth?.count ? 'text-brand-700 dark:text-brand-400' : 'text-subtle'}`}>{inr(stats?.paidThisMonth?.amount)}</span>}
          hint={stats?.paidThisMonth?.count ? `${stats.paidThisMonth.count} disbursed since the 1st` : 'nothing paid yet this month'}
          active={status === 'DISBURSED'}
          onClick={() => pickStatus('DISBURSED', true)}
        />
        <StatCard
          label="Rejected this month"
          tone={stats?.rejectedThisMonth?.count ? 'danger' : 'neutral'}
          icon={<Ban />}
          value={<span className={`tabular-nums ${stats?.rejectedThisMonth?.count ? 'text-danger-600 dark:text-danger-500' : 'text-subtle'}`}>{inr(stats?.rejectedThisMonth?.amount)}</span>}
          hint={stats?.rejectedThisMonth?.count ? `${stats.rejectedThisMonth.count} turned down` : 'none turned down'}
          active={status === 'REJECTED'}
          onClick={() => pickStatus('REJECTED', true)}
        />
      </div>
      <Card>
        <DataTable
          storageKey="advance-requests-v2"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          onRow={(r: any) => ({ onClick: () => setDetailFor(r), style: { cursor: 'pointer' } })}
          rowClassName={focus.rowClassName}
          toolbarLeft={
            <Space wrap>
              <Input placeholder="Request no / purpose" style={{ width: 200 }}
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              {canSeeAll && (
                <Select showSearch optionFilterProp="label" allowClear placeholder="Field staff" style={{ width: 200 }}
                  value={repId} onChange={setRepId}
                  options={(salesPersons ?? []).map((s: any) => ({ value: String(s.repId), label: s.repName ?? `#${s.repId}` }))} />
              )}
              <Select placeholder="Status" allowClear style={{ width: 160 }} value={status} onChange={setStatus}
                options={Object.keys(STATUS_COLOR).map((s) => ({ value: s, label: STATUS_LABEL[s] ?? s }))} />
              <DatePicker.RangePicker disabledDate={disableFuture} value={range as any} onChange={(v) => setRange(v as any)} />
              <Checkbox checked={showHistory} onCheckedChange={(checked) => setShowHistory(checked)} label="Show full history" />
            </Space>
          }
        />
      </Card>

      <RequestModal open={requestOpen} onClose={() => setRequestOpen(false)} isHead={isHead} />
      {detailFor && (
        <RequestDetailModal
          req={detailFor}
          canDisburse={canDisburse}
          startPaying={payNow}
          onClose={() => { setDetailFor(null); setPayNow(false); }}
        />
      )}
    </div>
  );
}

function RejectButton({ onReject }: { onReject: (reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Button size="sm" variant="danger" aria-label="Reject the request" icon={<X size={16} />} onClick={() => setOpen(true)} />
      <Modal open={open} onOpenChange={(o) => { if (!o) setOpen(false); }} title="Reject request"
        footer={
          <>
            <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="danger" disabled={!reason.trim()} onClick={() => { onReject(reason.trim()); setOpen(false); setReason(''); }}>Reject</Button>
          </>
        }>
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (visible to the sales person)" />
      </Modal>
    </span>
  );
}

function RequestModal({ open, onClose, isHead }: { open: boolean; onClose: () => void; isHead: boolean }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | undefined>();
  const [purpose, setPurpose] = useState('');
  const [forUserId, setForUserId] = useState<string | undefined>();
  const [proof, setProof] = useState<string | undefined>();

  const { data: team } = useQuery({
    queryKey: ['advance-team'],
    queryFn: () => request<any[]>({ url: '/advance-requests/team-members' }),
    enabled: open && isHead,
  });

  const save = useConfirmedMutation({ title: "Send this advance request?", content: "It goes to Accounts to approve before any money moves.", okText: "Yes, send it" }, {
    mutationFn: () => request({
      url: '/advance-requests/request', method: 'POST',
      data: { amount: Number(amount), purpose, forUserId: isHead ? forUserId : undefined, requestProofUrl: proof },
    }),
    onSuccess: () => {
      message.success('Advance requested');
      qc.invalidateQueries({ queryKey: ['advance-requests'] });
      qc.invalidateQueries({ queryKey: ['advance-request-stats'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
      setAmount(undefined); setPurpose(''); setForUserId(undefined); setProof(undefined);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const label = (t: string, required?: boolean) => (
    <div className="mb-1 type-label text-muted">{t}{required && <span className="text-danger-500"> *</span>}</div>
  );
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Request advance"
      description="Company money — say how much, what for, and attach what backs it up."
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          {/* The document is as required as the amount: the server rejects a request without one,
              so the button refuses first rather than letting the rep fill a form that will bounce. */}
          <Button variant="primary" loading={save.isPending}
            disabled={!(Number(amount) > 0) || !purpose.trim() || !proof}
            onClick={() => save.mutate()}>Request</Button>
        </>
      }>
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* A Sales Head is field staff too, so the beneficiary defaults to THEMSELVES; the
            picker only exists to raise the request on behalf of a sales person in their team. */}
        {isHead && (
          <div className="w-full">
            {label('Who is this advance for?')}
            <Select showSearch optionFilterProp="label" className="w-full" placeholder="Who is this advance for?"
              value={forUserId ?? SELF}
              onChange={(v: any) => setForUserId(v === SELF ? undefined : v)}
              options={[
                { value: SELF, label: 'Myself' },
                ...(team ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName })),
              ]} />
          </div>
        )}
        <div className="w-full">
          {label('Amount', true)}
          <InputNumber min={1} placeholder="Amount ₹" style={{ width: '100%' }} value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        </div>
        <div className="w-full">
          {label('Purpose', true)}
          <Input.TextArea rows={2} placeholder="Purpose (e.g. 3-day Pune-east tour)" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </div>
        <div className="w-full">
          {label('Supporting document', true)}
          {/* Asking for company money is a claim, and a claim carries a document: the quotation, the
              fare estimate, the hotel booking, the bill about to be paid. Without one, an approver is
              being asked to trust a sentence, and nothing is left behind for whoever audits it later. */}
          <FileUploadField category="receipt" buttonLabel={proof ? 'Replace document' : 'Attach bill / quotation / screenshot'} value={proof} onChange={setProof} />
          <div className="mt-1 type-caption text-subtle">Required — the approver sees this before releasing money.</div>
        </div>
      </Space>
    </Modal>
  );
}

/**
 * One advance request, end to end — requested → approved → disbursed (or rejected), with who,
 * when and how at every step, AND the place where the money is actually handed over.
 *
 * Detail and disbursement used to be two dialogs, so paying a request meant reading it in one
 * window, dismissing that window, and re-reading the amount and the payee in a second one that had
 * to repeat them both. They are one thing — you look at a request in order to decide whether to pay
 * it — so this dialog turns over in place: same header, same summary band, and the lower half swaps
 * between the request's story and the payment form.
 */
export function RequestDetailModal({
  req, onClose, canDisburse, startPaying,
}: {
  req: any;
  onClose: () => void;
  /** Whether this viewer may hand the money over. Gates the Disburse step only. */
  canDisburse?: boolean;
  /** Open straight on the payment form — the register's row action wants to pay, not to read. */
  startPaying?: boolean;
}) {
  const qc = useQueryClient();
  const status = String(req.status ?? '');
  const payable = status === 'APPROVED' && !!canDisburse;
  const [paying, setPaying] = useState(!!startPaying && payable);
  const [mode, setMode] = useState('BANK_TRANSFER');
  const [ref, setRef] = useState('');
  const [bankId, setBankId] = useState<string | undefined>();
  const [proof, setProof] = useState<string | undefined>();
  /* Opens on what is due so the payer confirms a figure rather than looking one up and retyping it —
     retyping is how a payout comes to differ from its approval. */
  const [payAmount, setPayAmount] = useState<number | null>(null);

  // ACTIVE company accounts only, primary first — the same options endpoint the payment screens and
  // the Give-advance dialog use. The ledger rejects an inactive or unknown account, so offering one
  // would only produce an error. Fetched only once the form is actually on screen.
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: paying,
  });
  const save = useConfirmedMutation({ title: "Disburse this advance?", content: "The money is recorded as paid out and lands on the rep’s wallet.", okText: "Yes, disburse" }, {
    mutationFn: () => request({
      url: `/advance-requests/${req.id}/disburse`, method: 'PATCH',
      data: {
        paymentMode: mode, paymentReference: ref || undefined,
        // Cash leaves no account behind; anything else must name the one it came from.
        bankAccountId: mode === 'CASH' ? undefined : bankId,
        paymentProofUrl: proof,
        disburseAmount: payAmount ?? undefined,
      },
    }),
    onSuccess: () => {
      message.success('Advance disbursed');
      qc.invalidateQueries({ queryKey: ['advance-requests'] });
      qc.invalidateQueries({ queryKey: ['advance-request-stats'] });
      qc.invalidateQueries({ queryKey: ['wallets'] });
      // The disbursement moves money, so the fleet KPI tiles are stale too — without this
      // they keep reading the pre-disburse figure until a reload.
      qc.invalidateQueries({ queryKey: ['wallet-stats'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const paidWith = [MODE_LABEL[req.paymentMode] ?? req.paymentMode, req.paymentReference].filter(Boolean).join(' · ');
  const label = (t: string, required?: boolean) => (
    <div className="mb-1 type-label text-muted">{t}{required && <span className="text-danger-500"> *</span>}</div>
  );
  const F = ({ label: l, children }: { label: string; children: ReactNode }) => (
    <div className="min-w-0">
      <div className="type-label text-muted">{l}</div>
      <div className="mt-0.5 type-body-medium text-content">{children ?? <span className="text-subtle">—</span>}</div>
    </div>
  );
  /*
   * The two figures a payout turns on. `approvedAmount` is NULL on every request decided before the
   * approver could fund part of an ask from cash the rep already holds, and reads as the full amount —
   * so nothing historic changes value here.
   */
  const cashPart = Number(req.cashFundedAmount ?? 0);
  const payableAmount = req.approvedAmount == null ? Number(req.amount ?? 0) : Number(req.approvedAmount);
  useEffect(() => { if (paying) setPayAmount(payableAmount); }, [paying, payableAmount]);

  /** One step of the request's life: who did it and when, or a plain "not yet". */
  const Step = ({ label: l, name, role, at }: { label: string; name?: string | null; role?: string | null; at?: string | null }) => (
    <li className="flex flex-wrap items-baseline gap-x-2 py-1">
      <span className="w-24 shrink-0 type-caption text-muted">{l}</span>
      {name
        ? (
          <>
            <span className="type-body-medium text-content">{name}</span>
            {roleTag(role)}
            <span className="type-caption text-subtle">{at ? formatIst(at) : ''}</span>
          </>
        )
        : <span className="type-caption text-subtle">not yet</span>}
    </li>
  );

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} width={640}
      title={<span>{req.requestNo} <Tag tone={STATUS_COLOR[status] as any}>{STATUS_LABEL[status] ?? status}</Tag></span>}
      description={paying
        ? 'Record how the money was handed over — this posts it into the wallet.'
        : 'Advance request — who asked, who approved, and how it was paid.'}
      footer={paying
        ? (
          <>
            <Button variant="default" onClick={() => setPaying(false)}>Back</Button>
            {/* A non-cash disbursement can't be saved without naming the account it came from — the
                ledger would refuse it anyway, so the button says so first. */}
            {/* A payout with no figure, or one above its approval, is not a payout this screen can
                record — the endpoint refuses both, and refusing here saves the round trip. */}
            <Button
              variant="primary" loading={save.isPending}
              disabled={!(Number(payAmount) > 0) || Number(payAmount) > payableAmount + 0.005
                || (mode !== 'CASH' && (!bankId || !proof))}
              onClick={() => save.mutate()}
            >Disburse</Button>
          </>
        )
        : (
          <>
            <Button variant="default" onClick={onClose}>Close</Button>
            {payable && (
              <Button variant="primary" icon={<Banknote size={16} />} onClick={() => setPaying(true)}>Disburse</Button>
            )}
          </>
        )}>
      {/* What is being asked for, in one line — it stays put across both halves, so the amount and
          the payee never leave the payer's sight while they fill the form in. */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 rounded-card border border-line bg-surface-2/50 px-4 py-3">
        {/* WHAT THIS PAYS, not what was asked for. They differ whenever the approver funded part of
            the ask from cash the rep was already carrying, and the payer is about to release exactly
            one of those two figures. The ask stays on screen underneath so the gap is explained
            rather than merely absent. */}
        <div>
          <div className="type-label text-muted">{cashPart > 0 ? 'To pay now' : 'Amount'}</div>
          <div className="type-metric tabular-nums text-brand-700 dark:text-brand-400">{inr2(payableAmount)}</div>
          {cashPart > 0 && (
            <div className="type-caption text-subtle">of {inr2(req.amount)} asked · {inr2(cashPart)} moves from their collection cash into the wallet</div>
          )}
        </div>
        <div className="min-w-0">
          <div className="type-label text-muted">Field staff</div>
          <div className="flex items-center gap-1.5 type-body-medium text-content">
            <UserLink id={req.repId} role={req.repRole}>{req.repName ?? `#${req.repId}`}</UserLink>
            {roleTag(req.repRole)}
          </div>
        </div>
        {/* The reason the money is being asked for — the one thing on this band the payer has to
            READ, not just glance at. It wraps. It used to `truncate` with nothing but a native
            `title` tooltip, so "Field trip to Sangamner belt — fuel a…" simply ended, and a hover
            tooltip is not an answer on a touch screen or to anyone who doesn't think to hover. */}
        <div className="min-w-0 max-w-[280px]">
          <div className="type-label text-muted">Purpose</div>
          <div className="whitespace-pre-wrap break-words type-body text-content">{req.purpose || '—'}</div>
        </div>
      </div>

      {paying ? (
        <>
          {/* The document the rep sent to justify the ask, right above the form that pays it — the
              payer should not have to remember what they saw on the previous screen. */}
          <div className="mb-3 flex items-center gap-3 rounded-card border border-line px-3 py-2">
            <AttachmentCell files={req.requestProofUrl ? [req.requestProofUrl] : []} label="Supporting document" size={44} />
            <div className="min-w-0">
              <div className="type-label text-muted">Supporting document</div>
              {/* The SAME sentence the record view uses two clicks away. It said "not attached" there
                  and "predates the attachment rule" here, for one fact about one document — and the
                  neutral half left the payer wondering whether the rep had simply skipped it. Proof is
                  gated at request time, so an empty one really is old, and both places now say so. */}
              <div className="type-caption text-subtle">{PROOF_NOTE(req.requestProofUrl)}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            {/* What is actually being handed over, for every mode. It opens on what is due, so the
                ordinary payout is a glance and a click; the endpoint caps it at the approved figure,
                because a payout may come in under its approval but never over it. */}
            <div className="sm:col-span-2">
              {label('Amount to disburse', true)}
              <InputNumber
                className="w-full" min={0} max={payableAmount} step={1} prefix="₹"
                value={payAmount} onChange={(v) => setPayAmount(v as number)}
              />
              <div className="mt-1 type-caption text-subtle">
                {payAmount != null && payAmount + 0.005 < payableAmount
                  ? `${inr2(payableAmount - payAmount)} less than approved — the request closes at what you pay.`
                  : `Approved: ${inr2(payableAmount)}. Pay less if that is what left; it cannot be more.`}
              </div>
            </div>
            <div>
              {label('Paid via', true)}
              <Select className="w-full" value={mode} onChange={(v) => { setMode(v); if (v === 'CASH') setBankId(undefined); }}
                options={PAYMENT_MODES.map((m) => ({ value: m, label: MODE_LABEL[m] ?? m }))} />
            </div>
            {/* Which company account the advance leaves from. Cash belongs to no account, so the
                picker only applies to the other modes. */}
            {mode !== 'CASH' && (
              <div>
                {label('From which bank account', true)}
                {/* WHICH ONE IS THE PRIMARY ACCOUNT — said on the option, not left to be known.
                    `/company/bank-accounts/options` already returns `isPrimary` and sorts the primary
                    first, but this picker built its own plain-text label and dropped the flag, so the
                    payer chose between "DEMO BANK · ····8901" and "HDFC BANK LIMITED · ····1057" with
                    nothing to tell them apart. `bankAcctOption` is the app's shared builder — the same
                    green Primary chip the Bank Details page and every payment form show — so the
                    account reads the same wherever it is offered. `searchLabel` keeps typing working. */}
                <Select
                  className="w-full" placeholder="Company bank account" value={bankId} onChange={setBankId}
                  showSearch optionFilterProp="searchLabel"
                  options={(bankAccounts ?? []).map(bankAcctOption)}
                  notFoundContent="No active company bank account — add or reactivate one in Company & Billing setup"
                />
              </div>
            )}
            <div className={mode === 'CASH' ? '' : 'sm:col-span-2'}>
              {label(mode === 'CASH' ? 'Reference (optional)' : 'Ref / UTR')}
              <Input className="w-full" placeholder={mode === 'CASH' ? 'Voucher no, if any' : 'Transfer reference'} value={ref} onChange={(e) => setRef(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              {label('Payment proof', mode !== 'CASH')}
              {/* Required for every non-cash rail: UPI, a transfer, a cheque and a card all leave a
                  receipt, and that receipt is what settles a later "the money never reached me". The
                  upload previews it in place, so the payer can see they attached the right document
                  before committing the money. Cash has no such artefact and stays optional. */}
              <FileUploadField category="receipt" buttonLabel={proof ? 'Replace proof' : 'Upload receipt / screenshot'} value={proof} onChange={setProof} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="type-label text-muted">Timeline</div>
          <ol className="mb-4 mt-1">
            <Step label="Requested" name={req.repName ?? `#${req.repId}`} role={req.repRole} at={req.createdAt} />
            <Step label="Approved" name={req.approvedByName} role={req.approvedByRole} at={req.approvedAt} />
            <Step label="Disbursed" name={req.disbursedByName} role={req.disbursedByRole} at={req.disbursedAt} />
          </ol>

          {/* Two different attachments, and a reader must never confuse them: what the rep sent to
              justify the ask, and what the payer sent to prove the money moved. */}
          <div className="mb-4 grid grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <div className="type-label text-muted">Supporting document</div>
              <div className="mt-1 flex items-center gap-2">
                <AttachmentCell files={req.requestProofUrl ? [req.requestProofUrl] : []} label="Supporting document" size={56} />
              </div>
              <div className="mt-1 type-caption text-subtle">{PROOF_NOTE(req.requestProofUrl)}</div>
            </div>
            {(paidWith || req.paymentProofUrl) && (
              <div>
                <div className="type-label text-muted">Payment proof</div>
                <div className="mt-1">
                  <AttachmentCell files={req.paymentProofUrl ? [req.paymentProofUrl] : []} label="Payment proof" size={56} />
                </div>
                <div className="mt-1 type-caption text-subtle">receipt for the payout</div>
              </div>
            )}
          </div>

          {/* The payment facts only exist once someone has paid. On a request still waiting for
              money, em-dashes under "Paid via" say nothing; what helps is naming what happens next. */}
          {paidWith ? (
            <F label="Paid via">{paidWith}</F>
          ) : status === 'APPROVED' ? (
            <div className="rounded-card border border-dashed border-line px-3 py-2 type-caption text-muted">
              Approved, but not paid yet — disbursing it records how the money was handed over and posts it to {req.repName ?? 'the rep'}&rsquo;s wallet.
            </div>
          ) : status === 'REQUESTED' ? (
            <div className="rounded-card border border-dashed border-line px-3 py-2 type-caption text-muted">
              Waiting for approval. Nothing has been paid.
            </div>
          ) : null}

          {status === 'REJECTED' && (
            <div className="mt-3">
              <div className="type-label text-muted">Reject reason</div>
              <div className="mt-0.5 type-body-medium text-danger-600 dark:text-danger-500">{req.rejectReason || 'Rejected'}</div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
